/**
 * Rotations Plugin for LightweightCharts v5 (Renko Rotations chart)
 *
 * Render-only overlay for the renko rotation model. It does NO swing/leg/bias math:
 * the backend (calculations/orderflow/rotation_legs.py + rotation_bias.py, reusing the
 * canonical swing detector) computes the swings, legs, per-leg reach tier, the 70/90
 * leg-size thresholds, and the directional-bias segments; the page maps each
 * leg/segment's brick index to the chart's synthetic brick timestamp and hands this
 * plugin ready-to-draw coordinates.
 *
 * Draws:
 *   - the zig-zag legs (line between consecutive swing pivots) + a pivot dot, coloured
 *     by direction (up green / down red). When showReach is on, each leg's line
 *     WEIGHT + DASH encodes its reach tier vs the per-direction 70/90 leg-size
 *     thresholds: extended (>p90) bold solid, typical (>p70) normal solid, short
 *     (<=p70) thin dotted. So reach is readable at every historic rotation.
 *   - 50/70/90 reach lines (store.reachLines, gated on showReach): per-pivot horizontal
 *     dashed lines at each rotation's start price +/- p50 (median "typical reach", teal) /
 *     p70 (slate) / p90 (amber) (in that rotation's
 *     direction), each spanning ONLY its own rotation (x0..x1) so they are localized at
 *     every historic pivot rather than smeared full-width; the current (developing)
 *     rotation's lines are flagged `forward` and run from the latest pivot to the right
 *     edge (both directions), labelled.
 *   - a thin directional-BIAS ribbon along the top of the pane, segmented by pivot
 *     spans and coloured by bias state, opacity scaled by |score|; the trailing live
 *     segment extends to the right edge.
 *
 * Store contract (set via setData):
 *   legs: [{ x0, y0, x1, y1, direction, reach }]   x* = brick display time (unix sec)
 *   reachLines: [{ x0, x1|null, price, level:50|70|90, forward:bool, label? }]
 *   biasSegments: [{ x0, x1, state, score, live }]  x* = brick display time
 *   gauge: { xPivot, yPivot, xExtreme, yExtreme, yCurrent,      // display-mapped by the page
 *            reversalLevel, pReversal, direction } | null       // r/theta retracement gauge
 *   showBias: bool      // draw the top ribbon
 *   showReach: bool      // weight/dash legs by reach + draw the 70/90 reach lines
 *   showGauge: bool      // draw the forming leg + retrace tail + reversal tick (descriptive-only)
 *
 * Legs / reach lines / bias segments are clipped to the visible time range (dense X=10
 * populations otherwise draw many off-screen segments).
 */

const DEFAULTS = {
  upColor: '#22c55e',
  downColor: '#ef4444',
  pivotRadiusPx: 3,
  legWidthPx: 1.5,
  ribbonHeightPx: 8,
  band50Color: 'rgba(45, 212, 191, 0.9)',   // teal -- median ("typical reach")
  band70Color: 'rgba(148, 163, 184, 0.9)',  // slate
  band90Color: 'rgba(234, 179, 8, 0.9)',    // amber
  gaugeColor: 'rgba(167, 139, 250, 0.95)',  // violet -- retracement-gauge reversal-level marker
  bandWidthPx: 1,
  labelFont: '11px sans-serif',
};

// Bias state -> base colour for the ribbon (opacity comes from |score|).
const STATE_COLORS = {
  bullish: '#22c55e',
  weak_bull: '#4ade80',
  bearish: '#ef4444',
  weak_bear: '#f87171',
  neutral_broadening: '#64748b',
  neutral_contracting: '#94a3b8',
  neutral_equal: '#475569',
  forming: '#334155',
};

// reach tier -> line weight (CSS px) + dash pattern (CSS px, null = solid).
function _reachStyle(reach, base) {
  if (reach === 'extended') return { width: base * 2, dash: null };
  if (reach === 'short') return { width: 1, dash: [2, 3] };
  return { width: base, dash: null };   // 'typical' or unknown -> normal solid
}

class RotationsRenderer {
  constructor(store, series, chart) {
    this.store = store;
    this.series = series;
    this.chart = chart;
  }

  draw(target) {
    const { series, chart, store } = this;
    if (!series || !chart) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hpr = scope.horizontalPixelRatio;
      const vpr = scope.verticalPixelRatio;
      const fullW = scope.bitmapSize ? scope.bitmapSize.width : scope.mediaSize.width * hpr;
      const ts = chart.timeScale();

      // Visible-range clip: skip segments whose whole [x0,x1] span sits off-screen so dense X=10
      // populations (~30 pivots / ~90 segments/day) don't draw off the visible window. `x*` are
      // display seconds; vr.from/vr.to are the leftmost/rightmost visible bar times. null vr
      // (chart not ready) -> draw everything (unchanged behaviour). Forward/live segments span to
      // the right edge, so they are only skipped when their anchor is past the right edge.
      const vr = ts.getVisibleRange();
      const spanInView = (a, b) => {
        if (!vr) return true;
        return Math.max(a, b) >= vr.from && Math.min(a, b) <= vr.to;
      };
      const anchorInView = (a) => (!vr ? true : a <= vr.to);

      // ── Zig-zag legs + pivot dots (weight/dash = reach) ────────────────
      const legs = store.legs || [];
      const baseW = store.legWidthPx || 1.5;
      for (let i = 0; i < legs.length; i++) {
        const lg = legs[i];
        if (!spanInView(lg.x0, lg.x1)) continue;
        const x0 = ts.timeToCoordinate(lg.x0);
        const x1 = ts.timeToCoordinate(lg.x1);
        if (x0 === null || x1 === null) continue;
        const y0 = series.priceToCoordinate(lg.y0);
        const y1 = series.priceToCoordinate(lg.y1);
        if (y0 === null || y1 === null) continue;
        const color = lg.direction === 'up' ? store.upColor : store.downColor;
        const st = store.showReach ? _reachStyle(lg.reach, baseW) : { width: baseW, dash: null };
        ctx.lineWidth = Math.max(1, Math.round(st.width * hpr));
        ctx.setLineDash(st.dash ? st.dash.map((d) => Math.round(d * hpr)) : []);
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(x0 * hpr, y0 * vpr);
        ctx.lineTo(x1 * hpr, y1 * vpr);
        ctx.stroke();
        ctx.setLineDash([]);
        // pivot dot at the leg end
        const r = Math.max(1, Math.round((store.pivotRadiusPx || 3) * hpr));
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x1 * hpr, y1 * vpr, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── 50/70/90 reach lines (per pivot, localized to each rotation) ──────
      // store.reachLines: [{ x0, x1|null, price, level:50|70|90, forward, label? }].
      // Historic lines span just their rotation (x0..x1); the developing rotation's
      // lines are `forward` and run from the latest pivot to the right edge (labelled).
      const reachLines = store.reachLines || [];
      if (store.showReach && reachLines.length) {
        ctx.lineWidth = Math.max(1, Math.round((store.bandWidthPx || 1) * hpr));
        ctx.font = store.labelFont;
        ctx.textBaseline = 'middle';
        const dash = [Math.round(4 * hpr), Math.round(4 * hpr)];
        for (let i = 0; i < reachLines.length; i++) {
          const ln = reachLines[i];
          if (ln.price == null || !isFinite(ln.price)) continue;
          // Clip off-screen historic lines (forward lines span to the right edge -> anchor-only test).
          if (ln.forward ? !anchorInView(ln.x0) : !spanInView(ln.x0, ln.x1)) continue;
          const ca = ts.timeToCoordinate(ln.x0);
          if (ca === null) continue;
          const xa = ca * hpr;
          let xb;
          if (ln.forward) {
            xb = fullW;
          } else {
            const cb = ts.timeToCoordinate(ln.x1);
            if (cb === null) continue;
            xb = cb * hpr;
          }
          if (xb <= xa) xb = xa + Math.max(1, hpr);
          const y = series.priceToCoordinate(ln.price);
          if (y === null) continue;
          const yb = Math.round(y * vpr);
          const color = ln.level === 90 ? store.band90Color
            : ln.level === 50 ? store.band50Color : store.band70Color;
          ctx.strokeStyle = color;
          ctx.setLineDash(dash);
          ctx.beginPath();
          ctx.moveTo(xa, yb);
          ctx.lineTo(xb, yb);
          ctx.stroke();
          ctx.setLineDash([]);
          if (ln.label) {
            ctx.fillStyle = color;
            ctx.fillText(ln.label, xa + Math.round(6 * hpr), yb - Math.round(7 * hpr));
          }
        }
      }

      // ── Retracement gauge: forming leg + retrace tail (descriptive-only) ───
      // The LIVE (unconfirmed) leg draws dashed from the last confirmed pivot to the running
      // extreme in the leg's direction color; a violet dashed tail from the extreme to the
      // current price shows the pullback's progress toward the reversal level, which is
      // marked only by a short right-edge tick (a full-width line reads as a false S/R
      // level). Pct label = clamped r/theta. Null-guard every coordinate (off-grid -> null).
      const gauge = store.gauge;
      if (store.showGauge && gauge && gauge.reversalLevel != null && isFinite(gauge.reversalLevel)) {
        const dirColor = gauge.direction === 'up' ? store.upColor : store.downColor;
        const yExtC = gauge.yExtreme != null ? series.priceToCoordinate(gauge.yExtreme) : null;
        const xExtC = gauge.xExtreme != null ? ts.timeToCoordinate(gauge.xExtreme) : null;
        const xExt = (xExtC !== null && xExtC !== undefined) ? xExtC * hpr : null;
        const yExt = yExtC !== null ? Math.round(yExtC * vpr) : null;
        // 1. Forming leg: last confirmed pivot -> running extreme (dashed, direction color).
        if (xExt !== null && yExt !== null && gauge.xPivot != null && gauge.yPivot != null) {
          const xPivC = ts.timeToCoordinate(gauge.xPivot);
          const yPivC = series.priceToCoordinate(gauge.yPivot);
          if (xPivC !== null && yPivC !== null) {
            ctx.lineWidth = Math.max(1, Math.round((store.legWidthPx || 1.5) * hpr));
            ctx.strokeStyle = dirColor;
            ctx.globalAlpha = 0.75;
            ctx.setLineDash([Math.round(5 * hpr), Math.round(4 * hpr)]);
            ctx.beginPath();
            ctx.moveTo(xPivC * hpr, Math.round(yPivC * vpr));
            ctx.lineTo(xExt, yExt);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
          }
        }
        // 2. Retrace tail: extreme -> current price at the live edge (violet) + tip dot + pct.
        const yCurC = gauge.yCurrent != null ? series.priceToCoordinate(gauge.yCurrent) : null;
        if (yCurC !== null && yExt !== null && xExt !== null) {
          const xTip = fullW - Math.round(10 * hpr);
          const yCur = Math.round(yCurC * vpr);
          ctx.lineWidth = Math.max(1, Math.round((store.bandWidthPx || 1) * hpr));
          ctx.strokeStyle = store.gaugeColor;
          ctx.setLineDash([Math.round(2 * hpr), Math.round(2 * hpr)]);
          ctx.beginPath();
          ctx.moveTo(xExt, yExt);
          ctx.lineTo(xTip, yCur);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = store.gaugeColor;
          ctx.beginPath();
          ctx.arc(xTip, yCur, Math.max(2, Math.round(2.5 * hpr)), 0, Math.PI * 2);
          ctx.fill();
          const pct = Math.round((gauge.pReversal || 0) * 100);
          ctx.font = store.labelFont;
          ctx.textBaseline = 'middle';
          ctx.fillText(`${pct}%`, xTip - Math.round(34 * hpr), yCur - Math.round(9 * hpr));
        }
        // 3. Reversal tick: short dash at the reversal level, right edge only.
        const yRevC = series.priceToCoordinate(gauge.reversalLevel);
        if (yRevC !== null) {
          const yRev = Math.round(yRevC * vpr);
          ctx.lineWidth = Math.max(1, Math.round(2 * hpr));
          ctx.strokeStyle = store.gaugeColor;
          ctx.beginPath();
          ctx.moveTo(fullW - Math.round(26 * hpr), yRev);
          ctx.lineTo(fullW - Math.round(4 * hpr), yRev);
          ctx.stroke();
        }
      }

      // ── Directional-bias ribbon (top strip) ────────────────────────────
      if (store.showBias) {
        const segs = store.biasSegments || [];
        const ribbonH = Math.max(1, Math.round((store.ribbonHeightPx || 8) * vpr));
        for (let i = 0; i < segs.length; i++) {
          const s = segs[i];
          // Live segment runs to the right edge (anchor-only test); historic segments clip by span.
          if (s.live ? !anchorInView(s.x0) : !spanInView(s.x0, s.x1)) continue;
          const c0 = ts.timeToCoordinate(s.x0);
          if (c0 === null) continue;
          const x0 = c0 * hpr;
          let x1;
          if (s.live) {
            x1 = fullW;   // trailing live segment runs to the right edge
          } else {
            const c1 = ts.timeToCoordinate(s.x1);
            if (c1 === null) continue;
            x1 = c1 * hpr;
          }
          if (x1 <= x0) x1 = x0 + Math.max(1, hpr);   // keep a visible sliver
          ctx.globalAlpha = 0.25 + 0.55 * Math.min(1, Math.abs(s.score || 0));
          ctx.fillStyle = STATE_COLORS[s.state] || STATE_COLORS.forming;
          ctx.fillRect(x0, 0, x1 - x0, ribbonH);
          ctx.globalAlpha = 1;
        }
      }
    });
  }
}

class RotationsPrimitiveView {
  constructor(store, series, chart) {
    this._renderer = new RotationsRenderer(store, series, chart);
  }
  zOrder() { return 'top'; }
  renderer() { return this._renderer; }
}

class RotationsPrimitive {
  constructor(store, series, chart) {
    this._view = new RotationsPrimitiveView(store, series, chart);
    this._requestUpdate = null;
    this._rafPending = false;
  }
  attached({ requestUpdate }) { this._requestUpdate = requestUpdate; }
  detached() { this._requestUpdate = null; this._rafPending = false; }
  updateAllViews() { return this._view; }
  paneViews() { return [this._view]; }
  scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }
}

export class RotationsPlugin {
  constructor(chart, series) {
    this.chart = chart;
    this.series = series;
    this.store = {
      ...DEFAULTS, legs: [], biasSegments: [], reachLines: [], gauge: null,
      showBias: false, showReach: false, showGauge: false,
    };
    this.primitive = null;
    this.enabled = false;
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.primitive = new RotationsPrimitive(this.store, this.series, this.chart);
    this.series.attachPrimitive(this.primitive);
  }

  disable() {
    if (this.primitive) {
      try { this.series.detachPrimitive(this.primitive); } catch (e) { /* ignore */ }
      this.primitive = null;
    }
    this.enabled = false;
  }

  isEnabled() { return this.enabled; }

  /** Replace the overlay data (legs + reach lines + bias segments + gauge, mapped to times). */
  setData({ legs, biasSegments, reachLines, gauge, showBias, showReach, showGauge } = {}) {
    if (legs !== undefined) this.store.legs = legs || [];
    if (biasSegments !== undefined) this.store.biasSegments = biasSegments || [];
    if (reachLines !== undefined) this.store.reachLines = reachLines || [];
    if (gauge !== undefined) this.store.gauge = gauge || null;
    if (showBias !== undefined) this.store.showBias = !!showBias;
    if (showReach !== undefined) this.store.showReach = !!showReach;
    if (showGauge !== undefined) this.store.showGauge = !!showGauge;
    if (this.primitive) this.primitive.scheduleUpdate();
  }
}

export default RotationsPlugin;
